import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { CustomButton } from '../../../components/common/customButton';
import { apiFormPost } from '../../../services/genesisApi/httpsMethods';

export interface TenPagerUploadDialogProps {
  open: boolean;
  dealId: number;
  onClose: () => void;
}

export default function TenPagerUploadDialog({ open, dealId, onClose }: TenPagerUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [responseText, setResponseText] = useState<string>('');

  const canUpload = useMemo(() => !!file && !uploading, [file, uploading]);

  const resetState = () => {
    setFile(null);
    setUploading(false);
    setErrorMessage(null);
    setResponseText('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setFile(nextFile);
    setErrorMessage(null);
    setResponseText('');
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setErrorMessage(null);
    setResponseText('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = `ten-pager?deal_id=${dealId}`;
      const response = await apiFormPost(endpoint, formData);

      if (!response?.success) {
        const message =
          typeof response?.message === 'string'
            ? response.message
            : response?.message?.message;
        setErrorMessage(message || 'Upload failed.');
        return;
      }

      const pretty =
        response.data === undefined
          ? ''
          : typeof response.data === 'string'
            ? response.data
            : JSON.stringify(response.data, null, 2);
      setResponseText(pretty);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Unexpected error uploading file.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={uploading ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Upload Ten Pager (Initial Materials)
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          disabled={uploading}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".xlsx,.xls"
          onChange={handleFileChange}
        />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
          <CustomButton
            text="Choose Excel File"
            handleButtonPress={handleChooseFile}
            solid={false}
            isButtonDisabled={uploading}
            widthOverride="180px"
          />

          <Typography sx={{ fontSize: 14, color: '#55575C' }}>
            {file ? file.name : 'No file selected.'}
          </Typography>
        </Box>

        {errorMessage ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 14, color: 'error.main', whiteSpace: 'pre-wrap' }}>
              {errorMessage}
            </Typography>
          </Box>
        ) : null}

        {responseText ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1, color: '#55575C' }}>
              API Response
            </Typography>
            <TextField
              value={responseText}
              fullWidth
              multiline
              minRows={12}
              maxRows={22}
              InputProps={{ readOnly: true }}
            />
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <CustomButton
          text="Close"
          handleButtonPress={handleClose}
          solid={false}
          isButtonDisabled={uploading}
        />
        <CustomButton
          text={uploading ? 'Uploading...' : 'Upload'}
          handleButtonPress={handleUpload}
          solid={true}
          isButtonDisabled={!canUpload}
          widthOverride="140px"
        />
      </DialogActions>
    </Dialog>
  );
}

